<template>
  <div class="corporate-form-container">
    <div class="form-wrapper">
      <!-- Page Header -->
      <div class="page-header">
        <div class="header-content">
          <div class="header-left">
            <h1 class="page-title">
              {{ isEditMode ? "Edit Corporate" : "Register Corporate" }}
            </h1>
            <p class="page-subtitle">
              {{
                isEditMode
                  ? "Update existing corporate tenant parameters"
                  : "Onboard a new corporate tenant with automated environment setup"
              }}
            </p>
          </div>
          <div class="header-actions">
            <button type="button" class="btn-secondary" @click="handleCancel">
              <i class="fas fa-arrow-left me-2"></i>Back to List
            </button>
            <button
              type="button"
              class="btn-primary"
              @click="handleSubmit"
              :disabled="isLoading"
            >
              <span v-if="isLoading"
                ><i class="fas fa-circle-notch fa-spin me-2"></i>Saving...</span
              >
              <span v-else
                ><i
                  :class="isEditMode ? 'fas fa-save' : 'fas fa-plus-circle'"
                  class="me-2"
                ></i
                >{{ isEditMode ? "Update" : "Create" }} Corporate</span
              >
            </button>
          </div>
        </div>
      </div>

      <!-- Error Message Banner -->
      <div v-if="errorMessage" class="error-banner">
        <div class="banner-icon">
          <i class="fas fa-exclamation-circle"></i>
        </div>
        <div class="banner-text">{{ errorMessage }}</div>
        <button class="banner-close" @click="errorMessage = ''">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Main Form Grid -->
      <div class="form-grid-layout">
        <!-- Left: Primary Details -->
        <div class="layout-main">
          <div class="form-section-card">
            <!-- Identity Section -->
            <div class="content-block">
              <div class="block-header">
                <i class="fas fa-id-card"></i>
                <div class="header-info">
                  <h3>Corporate Identity</h3>
                  <p>Unique identifiers and primary branding names</p>
                </div>
              </div>

              <div class="fields-grid">
                <div class="form-field-group">
                  <label>Corporate Code <span class="required">*</span></label>
                  <input
                    type="text"
                    v-model="corporate.code"
                    class="form-input"
                    placeholder="e.g. CORP-01"
                    :disabled="isEditMode"
                  />
                  <span v-if="isEditMode" class="field-hint"
                    >Code is locked after registration</span
                  >
                  <div v-if="validation.code" class="error-msg">
                    {{ validation.code[0] }}
                  </div>
                </div>

                <div class="form-field-group">
                  <label>Slug Name <span class="required">*</span></label>
                  <input
                    type="text"
                    v-model="corporate.slug_name"
                    class="form-input"
                    placeholder="e.g. acme-corp"
                  />
                  <span class="field-hint">Internal system reference name</span>
                  <div v-if="validation.slug_name" class="error-msg">
                    {{ validation.slug_name[0] }}
                  </div>
                </div>

                <div class="form-field-group full">
                  <label
                    >Full Corporate Name <span class="required">*</span></label
                  >
                  <input
                    type="text"
                    v-model="corporate.name"
                    class="form-input"
                    placeholder="e.g. Acme Corporation Ltd."
                  />
                  <div v-if="validation.name" class="error-msg">
                    {{ validation.name[0] }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Access & Domain Configuration -->
            <div class="content-block">
              <div class="block-header">
                <i class="fas fa-globe"></i>
                <div class="header-info">
                  <h3>Domain Configuration</h3>
                  <p>Choose how this tenant will be accessed publicly</p>
                </div>
              </div>

              <!-- Refined Domain Type Selector -->
              <div class="domain-mode-selector">
                <div
                  class="mode-card"
                  :class="{ active: subdomainType }"
                  @click="
                    subdomainType = true;
                    domainType = false;
                  "
                >
                  <div class="mode-check">
                    <div class="check-inner"></div>
                  </div>
                  <div class="mode-info">
                    <span class="mode-name">Subdomain</span>
                    <span class="mode-desc"
                      >Standard access (e.g. tenant.mymembership.id)</span
                    >
                  </div>
                </div>

                <div
                  class="mode-card"
                  :class="{ active: domainType }"
                  @click="
                    domainType = true;
                    subdomainType = false;
                  "
                >
                  <div class="mode-check">
                    <div class="check-inner"></div>
                  </div>
                  <div class="mode-info">
                    <span class="mode-name">Custom Domain</span>
                    <span class="mode-desc"
                      >Branded white-label (e.g. mycorp.com)</span
                    >
                  </div>
                </div>
              </div>

              <div v-if="subdomainType" class="fields-grid mt-4">
                <div class="form-field-group full">
                  <label>Subdomain Prefix</label>
                  <div class="field-append-group">
                    <input
                      type="text"
                      v-model="corporate.custom_subdomain"
                      class="form-input"
                      placeholder="acme"
                    />
                    <span class="append-text">{{ domainSuffix }}</span>
                  </div>
                  <div v-if="validation.custom_subdomain" class="error-msg">
                    {{ validation.custom_subdomain[0] }}
                  </div>
                </div>
              </div>

              <div v-if="domainType" class="fields-grid mt-4">
                <div class="form-field-group full">
                  <label>Custom Domain Address</label>
                  <input
                    type="text"
                    v-model="corporate.custom_domain"
                    class="form-input"
                    placeholder="www.mycorporate.com"
                  />
                  <div v-if="validation.custom_domain" class="error-msg">
                    {{ validation.custom_domain[0] }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Address & Location -->
            <div class="content-block">
              <div class="block-header">
                <i class="fas fa-map-marked-alt"></i>
                <div class="header-info">
                  <h3>Location Details</h3>
                  <p>Specified address for invoices and identity</p>
                </div>
              </div>

              <div class="fields-grid">
                <div class="form-field-group">
                  <label>City/Area <span class="required">*</span></label>
                  <input
                    type="text"
                    v-model="corporate.city"
                    class="form-input"
                    placeholder="e.g. Central Jakarta"
                    @input="
                      corporate.city = $event.target.value.replace(
                        /[^a-zA-Z\s\-.]/g,
                        ''
                      )
                    "
                  />
                  <div v-if="validation.city" class="error-msg">
                    {{ validation.city[0] }}
                  </div>
                </div>

                <div class="form-field-group">
                  <label>State/Province <span class="required">*</span></label>
                  <input
                    type="text"
                    v-model="corporate.state"
                    class="form-input"
                    placeholder="e.g. DKI Jakarta"
                    @input="
                      corporate.state = $event.target.value.replace(
                        /[^a-zA-Z\s\-.]/g,
                        ''
                      )
                    "
                  />
                  <div v-if="validation.state" class="error-msg">
                    {{ validation.state[0] }}
                  </div>
                </div>

                <div class="form-field-group">
                  <label>Postcode <span class="required">*</span></label>
                  <input
                    type="text"
                    v-model="corporate.postcode"
                    class="form-input"
                    placeholder="e.g. 10110"
                    @input="
                      corporate.postcode = $event.target.value.replace(
                        /[^0-9]/g,
                        ''
                      )
                    "
                  />
                  <div v-if="validation.postcode" class="error-msg">
                    {{ validation.postcode[0] }}
                  </div>
                </div>

                <div class="form-field-group">
                  <label>Country <span class="required">*</span></label>
                  <o-select v-model="corporate.country" :options="countries" />
                  <div v-if="validation.country" class="error-msg">
                    {{ validation.country[0] }}
                  </div>
                </div>

                <div class="form-field-group full">
                  <label>Detailed Address</label>
                  <textarea
                    v-model="corporate.address"
                    class="form-textarea"
                    rows="3"
                    placeholder="Full address details..."
                  ></textarea>
                  <div v-if="validation.address" class="error-msg">
                    {{ validation.address[0] }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Contact Information -->
            <div class="content-block">
              <div class="block-header">
                <i class="fas fa-phone-alt"></i>
                <div class="header-info">
                  <h3>Contact Channels</h3>
                  <p>Primary contact points for support and operations</p>
                </div>
              </div>

              <div class="fields-grid">
                <div class="form-field-group">
                  <label>Phone Number <span class="required">*</span></label>
                  <input
                    type="text"
                    v-model="corporate.phone"
                    class="form-input"
                    placeholder="+62..."
                    @input="
                      corporate.phone = $event.target.value.replace(
                        /[^0-9+\-\s()]/g,
                        ''
                      )
                    "
                  />
                  <div v-if="validation.phone" class="error-msg">
                    {{ validation.phone[0] }}
                  </div>
                </div>

                <div class="form-field-group">
                  <label>Fax <span class="required">*</span></label>
                  <input
                    type="text"
                    v-model="corporate.fax"
                    class="form-input"
                    placeholder="+62..."
                    @input="
                      corporate.fax = $event.target.value.replace(
                        /[^0-9+\-\s()]/g,
                        ''
                      )
                    "
                  />
                  <div v-if="validation.fax" class="error-msg">
                    {{ validation.fax[0] }}
                  </div>
                </div>

                <div class="form-field-group">
                  <label>Main Website (URL)</label>
                  <input
                    type="text"
                    v-model="corporate.website"
                    class="form-input"
                    placeholder="https://..."
                  />
                </div>

                <div class="form-field-group">
                  <label>Commerce Site (URL)</label>
                  <input
                    type="text"
                    v-model="corporate.commerce_site"
                    class="form-input"
                    placeholder="https://shop..."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right: Sidebar Assets -->
        <div class="layout-side">
          <div class="form-section-card sticky-box">
            <div class="content-block no-border">
              <div class="block-header">
                <i class="fas fa-image"></i>
                <div class="header-info">
                  <h3>Corporate Assets</h3>
                  <p>Branding and logo uploads</p>
                </div>
              </div>

              <div class="asset-upload-area">
                <div class="logo-preview-container">
                  <o-image-input
                    name="logo"
                    id="logo"
                    :imageurl="logoPreview"
                    @selectImage="handleLogoUpload"
                  />
                </div>
                <div class="asset-info">
                  <span class="asset-title">Primary Logo</span>
                  <p>
                    Recommended: Square format, PNG or WEBP with transparent
                    background (Max 5MB).
                  </p>
                </div>
                <div v-if="validation.logo" class="error-msg text-center">
                  {{ validation.logo[0] }}
                </div>
              </div>
            </div>
          </div>

          <!-- Status Card (Only for Edit) -->
          <div v-if="isEditMode" class="form-section-card mt-4">
            <div class="content-block no-border">
              <div class="block-header">
                <i class="fas fa-history"></i>
                <div class="header-info">
                  <h3>Registration Status</h3>
                  <p>Tenant environment status</p>
                </div>
              </div>
              <div class="status-summary">
                <div class="summary-item">
                  <span class="label">Status</span>
                  <span class="value success">Active</span>
                </div>
                <div class="summary-item">
                  <span class="label">Database</span>
                  <span class="value success">Provisioned</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Global Loading Overlay -->
    <div v-if="isLoading" class="form-loader">
      <div class="loader-content">
        <div class="spinner-border text-danger" role="status"></div>
        <p>Synchronizing environment...</p>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, ref, onMounted, computed, onBeforeUnmount } from "vue";
import { useRouter, useRoute } from "vue-router";
import api from "@/helpers/api";
import { OSelect, OImageInput } from "@/components/OmnihControl";
import { useToast } from "@/composables/useToast";

export default {
  name: "ExtranetCorporateForm",

  components: {
    OSelect,
    OImageInput,
  },

  setup() {
    const router = useRouter();
    const route = useRoute();
    const { showToast } = useToast();
    const isMounted = ref(true);

    const isLoading = ref(false);

    const validation = ref({});
    const errorMessage = ref("");
    const showDomainError = ref(false);

    const corporate = reactive({
      code: "",
      name: "",
      slug_name: "",
      address: "",
      state: "",
      country: "101",
      phone: "",
      fax: "",
      website: "",
      commerce_site: "",
      city: "",
      postcode: "",
      logo: "",
      custom_domain: "",
      custom_subdomain: "",
    });

    const domainType = ref(false);
    const subdomainType = ref(true);
    const logoPreview = ref("");

    const countries = ref([
      { value: "101", text: "Indonesia" },
      { value: "102", text: "Malaysia" },
      { value: "103", text: "Singapore" },
      { value: "104", text: "Thailand" },
    ]);

    const isEditMode = computed(() => !!route.params.id);

    const domainSuffix = computed(() => {
      const hostname = window.location.hostname;
      if (hostname === "localhost" || hostname.endsWith(".localhost")) {
        return ".localhost";
      }
      const parts = hostname.split(".");
      return parts.length > 1 ? "." + parts.slice(-2).join(".") : ".localhost";
    });

    function handleLogoUpload(imageData) {
      corporate.logo = imageData;
      logoPreview.value = imageData;
    }

    function validateForm() {
      validation.value = {};
      showDomainError.value = false;
      let isValid = true;

      const required = [
        "code",
        "name",
        "slug_name",
        "city",
        "state",
        "postcode",
        "country",
        "phone",
        "fax",
      ];
      required.forEach((field) => {
        if (!corporate[field]) {
          validation.value[field] = [`${field.replace("_", " ")} is required`];
          isValid = false;
        }
      });

      if (domainType.value && !corporate.custom_domain) {
        validation.value.custom_domain = ["Custom domain is required"];
        isValid = false;
      }

      if (subdomainType.value && !corporate.custom_subdomain) {
        validation.value.custom_subdomain = ["Subdomain prefix is required"];
        isValid = false;
      }

      return isValid;
    }

    async function handleSubmit() {
      if (!validateForm()) {
        errorMessage.value =
          "Validation failed. Please review the highlighted fields.";
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      isLoading.value = true;
      let isSuccess = false;

      try {
        const payload = {
          ...corporate,
          custom_domain: domainType.value ? corporate.custom_domain : "",
          custom_subdomain: subdomainType.value
            ? corporate.custom_subdomain
            : "",
          domainType: domainType.value,
          subdomainType: subdomainType.value,
        };

        if (isEditMode.value) {
          payload.id = route.params.id;
          await api.request(
            "put",
            `/api/extranet/corporate/${route.params.id}`,
            {},
            payload
          );
          showToast({
            type: "success",
            title: "Corporate Updated",
            message: "The organization profile has been successfully updated.",
          });
        } else {
          await api.request("post", "/api/extranet/corporate", {}, payload);
          showToast({
            type: "success",
            title: "Corporate Registered",
            message: "New tenant cluster has been successfully provisioned.",
          });
        }

        isSuccess = true;
      } catch (error) {
        if (error.response?.data?.errors) {
          validation.value = error.response.data.errors;
          errorMessage.value = "Required fields are missing or invalid.";
        } else {
          errorMessage.value =
            error.response?.data?.message ||
            "A system synchronization error occurred.";
        }
        window.scrollTo({ top: 0, behavior: "smooth" });
      } finally {
        isLoading.value = false;
      }

      // Redirect di luar try-catch agar navigation error tidak ter-catch sebagai API error
      if (isSuccess) {
        router.push({ name: "ExtranetCorporate" });
      }
    }

    function handleCancel() {
      router.push({ name: "ExtranetCorporate" });
    }

    async function loadCorporateData(id) {
      isLoading.value = true;
      try {
        const response = await api.request(
          "get",
          `/api/extranet/corporate/${id}`
        );
        const data = response.data.data;

        // Determine domain type based on available data
        if (data.custom_subdomain) {
          subdomainType.value = true;
          domainType.value = false;
        } else if (data.custom_domain) {
          domainType.value = true;
          subdomainType.value = false;
        }

        Object.assign(corporate, {
          code: data.code || "",
          name: data.name || "",
          slug_name: data.slug_name || "",
          address: data.business_profile?.address || "",
          state: data.business_profile?.state || "",
          country: data.business_profile?.country || "101",
          phone: data.business_profile?.phone || "",
          fax: data.business_profile?.fax || "",
          website: data.business_profile?.website || "",
          commerce_site: data.business_profile?.commerce_site || "",
          city: data.business_profile?.city || "",
          postcode: data.business_profile?.postcode || "",
          custom_domain: data.custom_domain || "",
          custom_subdomain: data.custom_subdomain || "",
        });

        if (data.business_profile?.logo?.image_url)
          logoPreview.value = data.business_profile.logo.image_url;
      } catch (error) {
        showToast({
          type: "error",
          title: "Loading Failed",
          message: "Unable to retrieve corporate profile.",
        });
        router.push({ name: "ExtranetCorporate" });
      } finally {
        isLoading.value = false;
      }
    }

    onMounted(() => {
      isMounted.value = true;
      if (isEditMode.value) loadCorporateData(route.params.id);
    });

    onBeforeUnmount(() => {
      isMounted.value = false;
    });

    return {
      isLoading,
      validation,
      errorMessage,
      showDomainError,
      corporate,
      domainType,
      subdomainType,
      logoPreview,
      countries,
      isEditMode,
      handleLogoUpload,
      handleSubmit,
      handleCancel,
      domainSuffix,
    };
  },
};
</script>

<style scoped>
.corporate-form-container {
  min-height: 100vh;
}

.form-wrapper {
  max-width: 1400px;
  margin: 0 auto;
}

/* Page Header */
.page-header {
  margin-bottom: 32px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
}

.page-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 12px;
}

/* Error Banner */
.error-banner {
  background: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: var(--extranet-rounded);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
}

.banner-icon {
  color: #ef4444;
  font-size: 18px;
}

.banner-text {
  flex: 1;
  color: #b91c1c;
  font-size: 14px;
  font-weight: 500;
}

.banner-close {
  background: transparent;
  border: none;
  color: #991b1b;
  cursor: pointer;
  padding: 4px;
  opacity: 0.6;
}

/* Grid Layout */
.form-grid-layout {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 32px;
  align-items: start;
}

.form-section-card {
  background: white;
  border-radius: var(--extranet-rounded);
  border: 1px solid var(--extranet-accent);
  padding: 40px;
}

.sticky-box {
  position: sticky;
  top: 32px;
}

/* Content Block Styling */
.content-block {
  padding-bottom: 40px;
  margin-bottom: 40px;
  border-bottom: 1px solid #f1f5f9;
}

.content-block:last-child,
.content-block.no-border {
  padding-bottom: 0;
  margin-bottom: 0;
  border-bottom: none;
}

.block-header {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
}

.block-header i {
  width: 42px;
  height: 42px;
  background: #fff5f5;
  color: var(--extranet-primary);
  border-radius: var(--extranet-rounded);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.header-info h3 {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
}

.header-info p {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

/* Fields Grid */
.fields-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

.form-field-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-field-group.full {
  grid-column: 1 / -1;
}

.form-field-group label {
  font-size: 13px;
  font-weight: 700;
  color: #475569;
}

.required {
  color: var(--extranet-primary);
}

.form-input,
.form-textarea {
  padding: 12px 16px;
  border: 1px solid var(--extranet-accent);
  border-radius: var(--extranet-rounded);
  font-size: 14px;
  color: #1e293b;
  transition: all 0.2s;
  width: 100%;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: var(--extranet-primary);
  box-shadow: 0 0 0 3px rgba(181, 26, 31, 0.05);
}

.form-input:disabled {
  background: #f8fafc;
  cursor: not-allowed;
  color: #94a3b8;
}

.field-hint {
  font-size: 12px;
  color: #94a3b8;
}

.error-msg {
  font-size: 12px;
  color: #ef4444;
  font-weight: 500;
}

/* Field Append Group */
.field-append-group {
  display: flex;
  align-items: stretch;
}

.field-append-group .form-input {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.append-text {
  padding: 12px 16px;
  background: #f1f5f9;
  border: 1px solid var(--extranet-accent);
  border-left: none;
  border-top-right-radius: var(--extranet-rounded);
  border-bottom-right-radius: var(--extranet-rounded);
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
}

/* Domain Mode Selector */
.domain-mode-selector {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 8px;
}

.mode-card {
  padding: 20px;
  border: 1px solid var(--extranet-accent);
  border-radius: var(--extranet-rounded);
  display: flex;
  gap: 16px;
  cursor: pointer;
  transition: all 0.2s;
  background: #ffffff;
}

.mode-card:hover {
  border-color: var(--extranet-primary);
  background: #fffafa;
}

.mode-card.active {
  border-color: var(--extranet-primary);
  background: #fff5f5;
  box-shadow: 0 4px 15px rgba(181, 26, 31, 0.08);
}

.mode-check {
  width: 20px;
  height: 20px;
  border: 2px solid #cbd5e1;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  margin-top: 2px;
}

.mode-card.active .mode-check {
  border-color: var(--extranet-primary);
}

.check-inner {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--extranet-primary);
  opacity: 0;
  transform: scale(0.5);
  transition: all 0.2s;
}

.mode-card.active .check-inner {
  opacity: 1;
  transform: scale(1);
}

.mode-name {
  display: block;
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
}

.mode-desc {
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

/* Asset Upload Styling */
.asset-upload-area {
  padding: 24px;
  background: #f8fafc;
  border-radius: var(--extranet-rounded);
  text-align: center;
}

.logo-preview-container {
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
}

.asset-title {
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
}

.asset-info p {
  font-size: 12px;
  color: #94a3b8;
  margin: 0;
  line-height: 1.6;
}

/* Status Summary */
.status-summary {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  padding: 10px 16px;
  background: #f8fafc;
  border-radius: var(--extranet-rounded);
}

.summary-item .label {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}

.summary-item .value {
  font-size: 13px;
  font-weight: 700;
}

.value.success {
  color: #059669;
}

/* Buttons */
.btn-primary,
.btn-secondary {
  padding: 12px 24px;
  border-radius: var(--extranet-rounded);
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
}

.btn-primary {
  background: var(--extranet-primary);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  filter: brightness(0.9);
  transform: translateY(-1px);
}

.btn-secondary {
  background: white;
  border: 1px solid var(--extranet-accent);
  color: #64748b;
}

.btn-secondary:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #1e293b;
}

/* Loader Overlay */
.form-loader {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loader-content {
  text-align: center;
}

.loader-content p {
  margin-top: 16px;
  font-weight: 600;
  color: #64748b;
}

/* OmnihControl Component Overrides */
:deep(.o-select select),
:deep(.o-select .vs__dropdown-toggle) {
  border-radius: var(--extranet-rounded) !important;
  border-color: var(--extranet-accent) !important;
}

:deep(.o-image-input),
:deep(.o-image-input .image-preview),
:deep(.o-image-input .upload-placeholder) {
  border-radius: var(--extranet-rounded) !important;
}

/* Responsive */
@media (max-width: 1200px) {
  .form-grid-layout {
    grid-template-columns: 1fr;
  }
  .sticky-box {
    position: static;
  }
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }
  .header-actions {
    width: 100%;
  }
  .header-actions .btn-primary,
  .header-actions .btn-secondary {
    flex: 1;
  }
  .fields-grid {
    grid-template-columns: 1fr;
  }
  .domain-mode-selector {
    grid-template-columns: 1fr;
  }
}
</style>
